import React, { useEffect, useState } from 'react';
import parseDataTable from './parseDataTable';
import datagridConverter from './datagridConverter';
import * as languageUtils from './languageUtils';
import FileSelector from './FileSelector';
import LinearProgress from '@mui/material/LinearProgress';
import Box from '@mui/material/Box';
import * as Utils from './utils';
import CustomGridToolbar from './CustomGridToolbar';

import {
	DataGrid,
	GridPagination,
	useGridApiContext,
	useGridSelector,
	gridVisibleColumnDefinitionsSelector,
} from '@mui/x-data-grid';

// Enhanced TableFooter that respects column visibility and widths
const TableFooter = ({ totals, language }) => {
	const apiRef = useGridApiContext();
	const visibleColumns = useGridSelector(apiRef, gridVisibleColumnDefinitionsSelector);
	const footerScrollRef = React.useRef(null);

	// Get column visibility model
	const columnVisibilityModel = useGridSelector(apiRef, (state) => state.columns.columnVisibilityModel);

	// Calculate total width for proper alignment
	const totalWidth = visibleColumns.reduce((sum, col) => sum + (col.computedWidth || col.width || 100), 0);

	// Sync scroll position with the data grid
	React.useEffect(() => {
		const setupScrollSync = () => {
			const virtualScroller = apiRef.current?.rootElementRef?.current?.querySelector(
				'.MuiDataGrid-virtualScroller'
			);
			const footerScrollContainer = footerScrollRef.current;

			if (!virtualScroller || !footerScrollContainer) {
				return null;
			}

			// Simple one-way sync: table scroll -> footer scroll
			const handleTableScroll = (e) => {
				if (footerScrollContainer) {
					footerScrollContainer.scrollLeft = e.target.scrollLeft;
				}
			};

			// Two-way sync: footer scroll -> table scroll
			const handleFooterScroll = (e) => {
				if (virtualScroller) {
					virtualScroller.scrollLeft = e.target.scrollLeft;
				}
			};

			virtualScroller.addEventListener('scroll', handleTableScroll, { passive: true });
			footerScrollContainer.addEventListener('scroll', handleFooterScroll, { passive: true });

			return () => {
				virtualScroller.removeEventListener('scroll', handleTableScroll);
				footerScrollContainer.removeEventListener('scroll', handleFooterScroll);
			};
		};

		// Try to setup immediately
		let cleanup = setupScrollSync();

		// If it failed, try again after a short delay (for initial mount)
		if (!cleanup) {
			const timeout = setTimeout(() => {
				cleanup = setupScrollSync();
			}, 100);

			// Try one more time after longer delay if still not ready
			const timeout2 = setTimeout(() => {
				if (!cleanup) {
					cleanup = setupScrollSync();
				}
			}, 500);

			return () => {
				clearTimeout(timeout);
				clearTimeout(timeout2);
			};
		}

		return cleanup;
	}, [apiRef, visibleColumns]);

	return (
		<Box sx={{ width: '100%' }}>
			{/* Standard pagination */}
			<GridPagination />

			{/* Scrollable container for custom totals row */}
			<Box
				ref={footerScrollRef}
				sx={{
					overflowX: 'auto',
					overflowY: 'hidden',
					width: '100%',
					'&::-webkit-scrollbar': {
						display: 'none',
					},
					scrollbarWidth: 'none', // Firefox
					msOverflowStyle: 'none', // IE/Edge
				}}
			>
				{/* Custom totals row */}
				<Box
					sx={{
						display: 'flex',
						borderTop: '2px solid #e0e0e0',
						backgroundColor: '#f5f5f5',
						fontWeight: 'bold',
						fontSize: '0.875rem',
						minHeight: 36,
						alignItems: 'center',
						width: totalWidth,
						position: 'relative',
					}}
				>
					{visibleColumns.map((col, index) => {
						const isFirstColumn = index === 0;
						const width = col.computedWidth || col.width || 100;

						return (
							<Box
								key={col.field}
								sx={{
									width: width,
									minWidth: width,
									maxWidth: width,
									padding: '0 16px',
									display: 'flex',
									alignItems: 'center',
									justifyContent:
										col.align === 'center'
											? 'center'
											: col.align === 'right'
											? 'flex-end'
											: 'flex-start',
									borderRight:
										index < visibleColumns.length - 1 ? '1px solid #e0e0e0' : 'none',
									height: '36px',
									overflow: 'hidden',
									textOverflow: 'ellipsis',
									whiteSpace: 'nowrap',
								}}
							>
								{isFirstColumn
									? language.total || 'TOTAL'
									: col.type === 'number' && typeof totals[col.field] === 'number'
									? // Format numbers based on column configuration
									  col.valueFormatter
										? col.valueFormatter({ value: totals[col.field] })
										: totals[col.field].toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ')
									: ''}
							</Box>
						);
					})}
				</Box>
			</Box>
		</Box>
	);
};

export default function TableModule({
	JSONData,
	language,
	options,
	hiddenColumns,
	columnTranslations,
	dimensionOrder,
}) {
	const [currentJSON, setCurrentJSON] = useState(null);
	const [gridData, setGridData] = useState({ columns: [], data: [] });
	const [languageObj, setLanguageObj] = useState(null);

	// Apply translations to columns
	const columns = React.useMemo(() => {
		const filteredColumns = gridData.columns.filter((column) => column.field != 'id');

		// Apply column translations
		const translatedColumns = filteredColumns.map((column) => {
			const originalTitle = column.title || column.headerName;
			let translatedTitle = originalTitle;

			// Priority 1: Custom translations passed as prop
			if (columnTranslations && columnTranslations[originalTitle]) {
				translatedTitle = columnTranslations[originalTitle];
			}
			// Priority 2: Built-in translations from language object
			else if (languageObj?.columnTranslations?.[originalTitle]) {
				translatedTitle = languageObj.columnTranslations[originalTitle];
			}

			return {
				...column,
				headerName: translatedTitle,
				originalTitle: originalTitle, // Keep original for hiding logic
			};
		});

		return Utils.autoSizeColumns(translatedColumns, gridData.data);
	}, [gridData.columns, languageObj, columnTranslations]);

	// Parse hiddenColumns parameter
	const columnVisibilityModel = React.useMemo(() => {
		if (!hiddenColumns || !columns.length) return {};

		// If it's a string, split by comma
		const columnsArray =
			typeof hiddenColumns === 'string'
				? hiddenColumns.split(',').map((col) => col.trim())
				: Array.isArray(hiddenColumns)
				? hiddenColumns
				: [];

		// Create visibility model object, matching by field, title, or originalTitle
		const visibilityModel = {};
		columnsArray.forEach((columnName) => {
			// Find column by field name, title, headerName, or originalTitle (untranslated)
			const matchingColumn = columns.find(
				(col) =>
					col.field === columnName ||
					col.title === columnName ||
					col.headerName === columnName ||
					col.originalTitle === columnName
			);

			if (matchingColumn) {
				visibilityModel[matchingColumn.field] = false;
			} else {
				// If no match found, try using it as-is (might be a field name we don't recognize)
				visibilityModel[columnName] = false;
			}
		});

		return visibilityModel;
	}, [hiddenColumns, columns]);

	const totals = React.useMemo(() => {
		const result = {};
		if (!gridData?.data?.length) return result;

		// Keywords that indicate a percentage column (in multiple languages)
		const percentageKeywords = [
			'percentage',
			'percent',
			'porcentaje',
			'porciento',
			'pourcentage',
			'pourcent',
			'percentagem',
			'percentual',
			'prozent',
			'prozentsatz',
			'%',
			'pct',
			'perc',
		];

		gridData.columns.forEach((col) => {
			if (col.type === 'number') {
				// Special handling for percentage columns (marked with isPercentage flag)
				if (col.isPercentage) {
					// Percentage columns should always show 100% in footer
					result[col.field] = 100;
				} else {
					// Check if column name contains percentage-related keywords
					const colNameLower = (col.field || col.title || col.headerName || '').toLowerCase();
					const hasPercentageKeyword = percentageKeywords.some((keyword) =>
						colNameLower.includes(keyword)
					);

					if (hasPercentageKeyword) {
						// Don't add to result - footer will show empty for these columns
						result[col.field] = '';
						return;
					}

					// Regular columns: sum all values
					result[col.field] = gridData.data.reduce((sum, row) => {
						// Remove thousand separators (spaces) before parsing
						const rawValue =
							typeof row[col.field] === 'string'
								? row[col.field].replace(/\s/g, '')
								: row[col.field];
						const val = parseFloat(rawValue);
						return sum + (isNaN(val) ? 0 : val);
					}, 0);
					result[col.field] = Number(result[col.field].toFixed(2));
				}
			}
		});

		return result;
	}, [gridData]);

	const handleOnChangeSelector = (json) => {
		if (json) {
			const processedData = parseDataTable(json, { dimensionOrder });
			const newGridData = datagridConverter(processedData, options);
			setGridData(newGridData);
			setCurrentJSON(json);
		}
	};

	const loadLanguage = async (_lang) => {
		const _langObj = await languageUtils.loadLocale(_lang);
		setLanguageObj(_langObj);
	};

	useEffect(() => {
		loadLanguage(language);
	}, [language]);

	if (!languageObj) {
		return (
			<div style={{ maxWidth: '100%' }}>
				<LinearProgress />
			</div>
		);
	}

	return (
		<div style={{ maxWidth: '100%' }}>
			<FileSelector
				JSONData={JSONData}
				onChange={handleOnChangeSelector}
				label={languageObj.fileSelectorLabel}
			/>
			{currentJSON && (
				<Box sx={{ height: 800, width: '100%' }}>
					<DataGrid
						columnBufferPx={100}
						rows={gridData.data}
						columns={columns}
						initialState={{
							pagination: {
								paginationModel: {
									pageSize: 25,
								},
							},
							columns: {
								columnVisibilityModel: columnVisibilityModel,
							},
						}}
						slots={{
							toolbar: CustomGridToolbar,
							footer: () => <TableFooter totals={totals} language={languageObj} />,
						}}
						pageSizeOptions={[10, 25, 50, 100]}
						sx={{
							height: '100%',
							'& p': {
								marginBottom: 0,
							},
							'& .MuiDataGrid-columnHeaders': {
								backgroundColor: '#f5f5f5',
								borderWidth: '2px',
							},
							'& .MuiDataGrid-columnHeaderTitle': {
								fontWeight: 700,
							},
						}}
						localeText={languageObj}
					/>
				</Box>
			)}
		</div>
	);
}
